import { MAX_PURCHASABLE_RATING, MIN_RATING, costForRating } from "@personnel/rules-leviathan";
import type { AttributeDef } from "@personnel/rules-leviathan";

interface AttributeRowProps {
  attribute: AttributeDef;
  rating: number;
  /** Minimum this attribute must reach to satisfy the balance rule. */
  floor: number;
  /** Faction rank title, shown on the Rank row when a faction is chosen. */
  title?: string | null;
  /** Highest rating this attribute may be raised to, when one applies. */
  maximum?: number | null;
  onChange: (rating: number) => void;
}

export function AttributeRow({
  attribute,
  rating,
  floor,
  title,
  maximum,
  onChange,
}: AttributeRowProps) {
  const aboveMaximum = maximum != null && rating > maximum;
  const belowFloor = !attribute.exemptFromBalance && rating < floor;
  const atMin = rating <= MIN_RATING;
  const atMax = rating >= Math.min(MAX_PURCHASABLE_RATING, maximum ?? MAX_PURCHASABLE_RATING);

  return (
    <div className="flex items-center gap-4 border-b border-void-800/60 py-3 last:border-b-0">
      <div
        className={`flex size-11 shrink-0 items-center justify-center rounded-full border-2 font-display text-lg font-semibold tabular-nums ${
          belowFloor || aboveMaximum
            ? "border-red-500/70 text-red-400"
            : "border-leviathan-600 text-leviathan-500"
        }`}
        aria-hidden
      >
        {rating}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-sm font-semibold tracking-wide text-void-200 uppercase">
            {attribute.name}
          </span>
          <span className="text-xs text-void-700 tabular-nums">
            {costForRating(rating)} Cost
          </span>
          {title != null && (
            <span className="truncate text-xs font-medium text-leviathan-500">{title}</span>
          )}
          {maximum != null && (
            <span
              className={`text-xs tabular-nums ${aboveMaximum ? "text-red-400" : "text-void-700"}`}
            >
              max {maximum}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-void-400" title={attribute.description}>
          {attribute.description}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(rating - 1)}
          disabled={atMin}
          aria-label={`Lower ${attribute.name}`}
          className="size-8 rounded-md border border-void-800 text-void-400 transition-colors hover:border-leviathan-600 hover:text-leviathan-500 disabled:cursor-not-allowed disabled:border-void-800/50 disabled:text-void-800"
        >
          −
        </button>
        <button
          type="button"
          onClick={() => onChange(rating + 1)}
          disabled={atMax}
          aria-label={`Raise ${attribute.name}`}
          className="size-8 rounded-md border border-void-800 text-void-400 transition-colors hover:border-leviathan-600 hover:text-leviathan-500 disabled:cursor-not-allowed disabled:border-void-800/50 disabled:text-void-800"
        >
          +
        </button>
      </div>
    </div>
  );
}
